import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_KEYCONTACTS_LOAD,
    ACTION_KEYCONTACTS_LOAD_BY_ID,
    ACTION_KEYCONTACTS_LOAD_FAILURE,
    ACTION_KEYCONTACTS_LOAD_SUCCESS,

    ACTION_KEYCONTACTS_SAVE,
    ACTION_KEYCONTACTS_SAVE_SUCCESS,
    ACTION_KEYCONTACTS_SAVE_FAILURE,

    ACTION_KEYCONTACTS_CREATE,
    ACTION_KEYCONTACTS_CREATE_SUCCESS,
    ACTION_KEYCONTACTS_CREATE_FAILURE,

    ACTION_KEYCONTACTS_VALIDATE,
    ACTION_KEYCONTACTS_VALIDATE_SUCCESS,
    ACTION_KEYCONTACTS_VALIDATE_FAILURE,

    ACTION_KEYCONTACTS_CLEAR,
    ACTION_KEYCONTACTS_FILTER,

    KeyContactAction
} from '../actions/keyContacts';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";


export interface IKeyContactState {
    formData: Dtos.KeyContact | Dtos.KeyContact[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.KeyContact[];
}

const initialState: IKeyContactState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: IKeyContactState, _isLoading: boolean): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IKeyContactState,
    formData: Dtos.KeyContact | Dtos.KeyContact[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IKeyContactState, formFilter: IFormFilter): IKeyContactState {

    const keycontacts: Dtos.KeyContact[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = keycontacts ? keycontacts.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IKeyContactState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: IKeyContactState, responseStatus: Dtos.ResponseStatus): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IKeyContactState, propertiesToClear?: string[]): IKeyContactState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IKeyContactState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IKeyContactState, _isSaving: boolean): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IKeyContactState,
    formData: Dtos.KeyContact | Dtos.KeyContact[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IKeyContactState, responseStatus: Dtos.ResponseStatus): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IKeyContactState, _isCreating: boolean): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IKeyContactState,
    formData: Dtos.KeyContact,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IKeyContactState, responseStatus: Dtos.ResponseStatus): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IKeyContactState, _isValidating: boolean): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        validatingState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IKeyContactState,
    formData: Dtos.KeyContact,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IKeyContactState, responseStatus: Dtos.ResponseStatus): IKeyContactState {
    const newState: IKeyContactState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const KeyContactReducer: Reducer<IKeyContactState> = (state: IKeyContactState = initialState, action: KeyContactAction) => {
    switch (action.type) {
        case ACTION_KEYCONTACTS_LOAD:
        case ACTION_KEYCONTACTS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_KEYCONTACTS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_KEYCONTACTS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_KEYCONTACTS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_KEYCONTACTS_SAVE:
            return saving(state, true);
        case ACTION_KEYCONTACTS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_KEYCONTACTS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_KEYCONTACTS_CREATE:
            return creating(state, true);
        case ACTION_KEYCONTACTS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_KEYCONTACTS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_KEYCONTACTS_VALIDATE:
            return validating(state, true);
        case ACTION_KEYCONTACTS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_KEYCONTACTS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_KEYCONTACTS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default KeyContactReducer;